// Cookie consent — storage, context, and hooks.
//
// Design:
//   - Consent is stored in localStorage under `cos:consent`.
//   - Shape: { version, ts, choices: { necessary, functional, analytics, marketing } }.
//   - Version bumps force re-prompt if we ever add or remove a category.
//   - `necessary` is ALWAYS true.
//   - `useConsent()` gives components reactive access.
//   - `hasConsented(cat)` is a pure helper that non-React code can call
//     (e.g. before dynamically injecting a script tag). Non-essential scripts
//     MUST call this before loading.
//   - A CustomEvent `cookieconsent:change` fires on every write so vanilla
//     integrations can react without a React re-render.

import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

export const CONSENT_STORAGE_KEY = "cos:consent";
export const CONSENT_VERSION = 2;

export const CATEGORIES = [
  {
    id: "necessary",
    label: "Essential",
    always: true,
    description:
      "Required to run CareerOS-AI — your login session, security tokens, and settings needed to render the page. These cannot be disabled.",
  },
  {
    id: "analytics",
    label: "Analytics",
    description:
      "Anonymous, aggregated measurement of which features are used and which are broken. Never captures your resume, chat, or profile content.",
  },
  {
    id: "performance",
    label: "Performance",
    description:
      "Page-timing, error, and speed metrics that help us keep CareerOS fast and reliable. Contains no personal content.",
  },
  {
    id: "marketing",
    label: "Marketing",
    description:
      "Personalisation and measurement for outreach or campaigns. We do not currently use marketing cookies — they will only load with your consent.",
  },
  {
    id: "functional",
    label: "Functional",
    description:
      "Remember your currency, timezone, dark or light theme, and other UI choices so we don't re-ask on every page.",
  },
];

const DEFAULT_CHOICES = {
  necessary: true,
  analytics: false,
  performance: false,
  marketing: false,
  functional: false,
};

// ---------- storage ----------
function readStore() {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(CONSENT_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || parsed.version !== CONSENT_VERSION) return null;
    return parsed;
  } catch {
    return null;
  }
}

function writeStore(choices) {
  if (typeof window === "undefined") return null;
  const record = {
    version: CONSENT_VERSION,
    ts: Date.now(),
    choices: { ...DEFAULT_CHOICES, ...choices, necessary: true },
  };
  try {
    window.localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(record));
    window.dispatchEvent(
      new CustomEvent("cookieconsent:change", { detail: record })
    );
  } catch {
    /* storage disabled — no-op */
  }
  return record;
}

// Pure helper — safe to call from non-React code.
export function hasConsented(category) {
  const rec = readStore();
  if (!rec) return category === "necessary";
  return Boolean(rec.choices?.[category]);
}

export function clearConsent() {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(CONSENT_STORAGE_KEY);
    window.dispatchEvent(
      new CustomEvent("cookieconsent:change", { detail: null })
    );
  } catch {
    /* noop */
  }
}

// ---------- context ----------
const CookieConsentContext = createContext(null);

export function CookieConsentProvider({ children }) {
  const [record, setRecord] = useState(() => readStore());
  const [managerOpen, setManagerOpen] = useState(false);

  // Cross-tab sync
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === CONSENT_STORAGE_KEY) setRecord(readStore());
    };
    const onChange = (e) => setRecord(e.detail || readStore());
    window.addEventListener("storage", onStorage);
    window.addEventListener("cookieconsent:change", onChange);
    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("cookieconsent:change", onChange);
    };
  }, []);

  const accept = useCallback((choices) => {
    const r = writeStore({ ...DEFAULT_CHOICES, ...choices, necessary: true });
    setRecord(r);
    setManagerOpen(false);
  }, []);

  const acceptAll = useCallback(() => {
    accept({ functional: true, analytics: true, performance: true, marketing: true });
  }, [accept]);

  const rejectNonEssential = useCallback(() => {
    accept({ functional: false, analytics: false, performance: false, marketing: false });
  }, [accept]);

  const openManager = useCallback(() => setManagerOpen(true), []);
  const closeManager = useCallback(() => setManagerOpen(false), []);

  const value = useMemo(
    () => ({
      record,
      hasDecided: !!record,
      choices: record?.choices || DEFAULT_CHOICES,
      accept,
      acceptAll,
      rejectNonEssential,
      openManager,
      closeManager,
      managerOpen,
    }),
    [record, accept, acceptAll, rejectNonEssential, openManager, closeManager, managerOpen]
  );

  return (
    <CookieConsentContext.Provider value={value}>
      {children}
    </CookieConsentContext.Provider>
  );
}

export function useConsent() {
  const ctx = useContext(CookieConsentContext);
  if (!ctx) {
    throw new Error("useConsent() must be used inside <CookieConsentProvider>");
  }
  return ctx;
}
